import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { Store } from '@ngrx/store';
import { appSettings } from 'app/appSettings';
import { CustomerInfoLoadIfSelectedGuard } from 'app/core/guards/customer-info-load-if-selected.guard';
import { DependencyTreeCanActivate } from 'app/core/guards/dependency-tree.guard';
import * as fromRoot from 'app/core/store/index';
import { UserContext } from 'app/core/store/session/user-context/user-context.interface';
import { logger } from 'app/core/util/logger';
import { firstValueFrom, of } from 'rxjs';
import { goAction } from '../../router/router.actions';
import { filter, first, switchMap } from 'rxjs/operators';
import { getFirst$$ } from '../../../util/util';
import { AuthenticationService } from '../../../authentication/authentication.service';

/**
 * Guard that customer is selected
 */
@Injectable({ providedIn: 'root' })
export class AuthorizationGuard implements CanActivate, DependencyTreeCanActivate {
  private authenticationService = inject(AuthenticationService);
  private store = inject(Store<fromRoot.RootState>);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    logger.debug('In AuthorizationGuard');
    // Is authenticated ?
    if (!(await getFirst$$(this.authenticationService.authenticated$))) {
      this.store.dispatch(goAction({ path: appSettings.PUBLIC_PAGE_ROUTE }));
      return false;
    }

    let authorizationChecker: (userContext: UserContext) => boolean;
    switch (route.data.authorization) {
      case 'isAdmin':
        authorizationChecker = userContext => userContext.isAdmin;
        break;
      case 'isAgent':
        authorizationChecker = userContext => userContext.isAgent;
        break;
      case 'canRelocate':
        authorizationChecker = userContext => userContext.canRelocate;
        break;
      case 'useEmailActors':
        authorizationChecker = userContext => userContext.useEmailActors;
        break;
      case 'canAccessHomeMarketBonus':
        authorizationChecker = userContext => userContext.canAccessHomeMarketBonus;
        break;
      case 'inviteEpsUser':
        authorizationChecker = userContext => userContext.isAdmin && userContext.adminRoles.inviteEpsUser;
        break;
      case 'inviteClientUser':
        authorizationChecker = userContext => userContext.isAdmin && userContext.adminRoles.inviteClientUser;
        break;
      case 'createClientUser':
        authorizationChecker = userContext => userContext.isAdmin && userContext.adminRoles.createClientUser;
        break;
      case 'updateEpsUser':
        authorizationChecker = userContext => userContext.isAdmin && userContext.adminRoles.updateEpsUser;
        break;
      case 'updateClientUser':
        authorizationChecker = userContext => userContext.isAdmin && userContext.adminRoles.updateClientUser;
        break;
    }

    let result: boolean;
    if (authorizationChecker) {
      result = await firstValueFrom(
        this.store.pipe(
          filter((root: fromRoot.RootState) => root.session.userContext.contextLoaded),
          first(),
          switchMap(root => {
            return of(authorizationChecker(root.session.userContext));
          })
        )
      );
    } else {
      result = await firstValueFrom(
        this.store.pipe(
          filter(state => state.session.authorization.loadSuccess || state.session.authorization.loadFailed),
          first(),
          switchMap(state => {
            let isAuthorized;
            if (route.data.createTemplate) {
              isAuthorized = route.data.createTemplate;
            } else {
              const authorizationPath = route.data.authorization.split('.');
              isAuthorized = authorizationPath.reduce((a, pathPart) => {
                return a && a[pathPart];
              }, (state.session.authorization.loadSuccess && state.session.authorization.data) || {});
            }

            return of(!!isAuthorized);
          })
        )
      );
    }
    logger.debug(() => `AuthorizationGuard result: ${result}`);
    return result;
  }

  getDependencies(): any {
    return [CustomerInfoLoadIfSelectedGuard];
  }

  getDependencyRouteData(): any {
    return {};
  }
}
